import type { RouteObject } from 'react-router';

/**
 * TODO EKP-3606: formalize ~/pages module and Page components
 **/
import { lazy, Suspense } from 'react';
import { defineMessage } from 'react-intl';

import type { BreadcrumbCrumb, RouteHandle } from '~/routing/types';
import type { User } from '~/types/User';

import { esignRedirectTabMessages } from '~/common/messages/esignRedirectTabMessages';
import { MAIN_CONTENT_ID } from '~/components/App/AppContents/constants';
import {
  useAdminClientNameCrumb,
  useAutomationClauseCrumb,
  useDashboardNameCrumb,
  useDataExtractionNameCrumb,
  useEntityGroupNameCrumb,
  useEntityTypeNameCrumb,
  useFieldModelNameCrumb,
  useLegacyTicketNameCrumb,
  useLegacyWorkflowNameCrumb,
  usePlaybookNameCrumb,
  useSavedSearchNameCrumb,
  useTicketDocumentNameCrumb,
  useTicketNameCrumb,
  useWorkflowNameCrumb,
} from '~/features/app/breadcrumbResolvers';
import { SidebarNavProvider } from '~/features/app/SidebarNavContext';
import { SidebarNavPreferenceProvider } from '~/features/app/SidebarNavPreferenceContext';
import { WorkflowDocumentsRedirect } from '~/features/workflows-clm-evolution/builder/panels/WorkflowDocumentsRedirect';
import { WorkflowSettingsRedirect } from '~/features/workflows-clm-evolution/builder/settings/WorkflowSettingsRedirect';
import { LegacyClmEvolutionRouteRedirect } from '~/features/workflows-clm-evolution/LegacyClmEvolutionRouteRedirect';
import { FlagType } from '~/flags/flags';
import { getFlag } from '~/flags/useFlag';
import { canUseAutomation } from '~/permissions/canUseAutomation';
import { canUseRestorationHub } from '~/permissions/canUseDocumentRestore';
import { canUseHome } from '~/permissions/canUseHome';
import { canUseManagedEntities } from '~/permissions/canUseManagedEntities';
import { canUsePlaybooks } from '~/permissions/canUsePlaybooks';
import { canUseSearchAsHomePage } from '~/permissions/canUseSearchAsHomePage';
import { canUseUpload } from '~/permissions/canUseUpload';
import { canUseUsersPage } from '~/permissions/canUseUsersPage';
import { canUseWorkflow } from '~/permissions/canUseWorkflow';
import { canUseWorkflowAdmin } from '~/permissions/canUseWorkflowAdmin';
import { checkIsAdmin } from '~/permissions/checkIsAdmin';
import { checkIsWorkflowOnly } from '~/permissions/checkIsWorkflowOnly';
import { checkIsRbac } from '~/permissions/rbac/checkIsRbac';
import { RoutePathType } from '~/routing/enums';
import { createAdminResolvers } from '~/routing/resolvers/createAdminResolvers';
import { createDefaultResolvers } from '~/routing/resolvers/createDefaultResolvers';
import { createIntegrateWorkflowIntakeFormResolvers } from '~/routing/resolvers/createIntegrateWorkflowIntakeFormResolvers';
import { createPassthroughResolvers } from '~/routing/resolvers/createPassthroughResolvers';
import { createSuperAdminResolvers } from '~/routing/resolvers/createSuperAdminResolvers';
import { createWorkflowAdminResolvers } from '~/routing/resolvers/createWorkflowAdminResolvers';
import { createWorkflowOrClmEvolutionResolvers } from '~/routing/resolvers/createWorkflowOrClmEvolutionResolvers';
import { createWorkflowResolvers } from '~/routing/resolvers/createWorkflowResolvers';

import { LoggedInLayout } from './layouts/LoggedInLayout';
import { LoggedOutLayout } from './layouts/LoggedOutLayout';
import { RouteGate } from './RouteGate';

const ClauseView = lazy(() =>
  import('../ClauseLibrary/ClauseView').then((m) => ({
    default: m.ClauseView,
  })),
);
const BlockCanvas = lazy(() =>
  import('~/features/workflows-clm-evolution/builder/BlockCanvas').then(
    (m) => ({
      default: m.BlockCanvas,
    }),
  ),
);
const TicketDetailsDocumentViewer = lazy(() =>
  import('~/features/workflows-clm-evolution/tickets/tickets-details/documents/document-viewer/TicketDetailsDocumentViewer').then(
    (m) => ({ default: m.TicketDetailsDocumentViewer }),
  ),
);
const NoMatch = lazy(() => import('~/pages/404'));
const ActivationPage = lazy(() => import('~/pages/activation'));
const ClientsPage = lazy(() => import('~/pages/admin'));
const AdminCreditsPage = lazy(() => import('~/pages/admin/[clientId]/credits'));
const AdminRolesPage = lazy(() => import('~/pages/admin/[clientId]/roles'));
const AdminUsersPage = lazy(() => import('~/pages/admin/[clientId]/users'));
const AdminNavsPage = lazy(() => import('~/pages/admin/console/[clientId]'));
const ApiManagementPage = lazy(
  () => import('~/pages/admin/console/[clientId]/api-management'),
);
const AuditLogsPage = lazy(
  () => import('~/pages/admin/console/[clientId]/audit-logs'),
);
const CopyWorkflowPage = lazy(
  () => import('~/pages/admin/console/[clientId]/copy-workflow'),
);
const CRMPage = lazy(() => import('~/pages/admin/console/[clientId]/crm'));
const DataFieldsPage = lazy(
  () => import('~/pages/admin/console/[clientId]/data-fields'),
);
const FieldManagerPage = lazy(
  () => import('~/pages/admin/console/[clientId]/field-manager'),
);
const EntityTypesPage = lazy(
  () => import('~/pages/admin/console/[clientId]/entity-types'),
);
const EntityTypeDetailPage = lazy(
  () => import('~/pages/admin/console/[clientId]/entity-types/[entityTypeId]'),
);
const DepartmentsPage = lazy(
  () => import('~/pages/admin/console/[clientId]/departments'),
);
const DepartmentPage = lazy(
  () => import('~/pages/admin/console/[clientId]/departments/[departmentId]'),
);
const DocumentManagementPage = lazy(
  () => import('~/pages/admin/console/[clientId]/document-management'),
);
const EmailIntakePage = lazy(
  () => import('~/pages/admin/console/[clientId]/email-intake'),
);
const IntegrationsPage = lazy(
  () => import('~/pages/admin/console/[clientId]/integrations'),
);
const PartyListsPage = lazy(
  () => import('~/pages/admin/console/[clientId]/manage-party-lists'),
);
const MoreSettingsPage = lazy(
  () => import('~/pages/admin/console/[clientId]/more-settings'),
);
const DocumentProcessingPage = lazy(
  () => import('~/pages/admin/console/[clientId]/process-documents'),
);
const ProcurementPage = lazy(
  () => import('~/pages/admin/console/[clientId]/procurement'),
);
const DocumentsRestore = lazy(
  () => import('~/pages/admin/console/[clientId]/restoration-hub'),
);
const SSOConfigPage = lazy(
  () => import('~/pages/admin/console/[clientId]/saml-configuration'),
);
const SAMLConfigPage = lazy(
  () =>
    import('~/pages/admin/console/[clientId]/saml-configuration/form/[configId]'),
);
const WebhooksPage = lazy(
  () => import('~/pages/admin/console/[clientId]/webhooks'),
);
const AgentForgeInvokePage = lazy(() => import('~/pages/agentforge-invoke'));
const AlertsPage = lazy(() => import('~/pages/alerts/index_deprecated'));
const AlertsPageV2 = lazy(() => import('~/pages/alerts'));

const AutomationHubPage = lazy(() => import('~/pages/automation'));
const AutomationHubDetailPage = lazy(() => import('~/pages/automation/detail'));
const RoutingTablePage = lazy(() => import('~/pages/automation/routing-table'));
const DynamicDataExtractionPage = lazy(
  () => import('~/pages/automation/dynamic-data-extraction'),
);
const ViewDataExtractionPage = lazy(
  () =>
    import('~/pages/automation/dynamic-data-extraction/view-data-extraction/[extractionId]'),
);
const ChangePasswordPage = lazy(() => import('~/pages/change-password'));
const ClauseLibrary = lazy(() => import('~/pages/clause-library'));
const TicketRerouteRedirectPage = lazy(() => import('~/pages/contract'));
const DashboardsPage = lazy(() => import('~/pages/dashboards/[dashboardId]'));
const DocumentRedirect = lazy(() => import('~/pages/document/'));
const DocumentsViewPage = lazy(() => import('~/pages/document/[documentId]'));
const DocumentsPage = lazy(() => import('~/pages/documents/[folderId]'));
const DocumentsEntityTypePage = lazy(
  () => import('~/pages/documents/entity-types/[entityTypeId]'),
);
const DocumentsEntityTypeGroupDetailPage = lazy(
  () =>
    import('~/pages/documents/entity-types/[entityTypeId]/entities/[groupId]'),
);
const DownloadEmailAttachmentPage = lazy(
  () => import('~/pages/download-email-attachment'),
);
const DownloadExportReportPage = lazy(
  () => import('~/pages/download-export-report'),
);
const TicketEmailApproveConfirmation = lazy(
  () => import('~/pages/email-action-approve-succeeds'),
);
const TicketEmailRejectConfirmation = lazy(
  () => import('~/pages/email-action-reject-succeeds'),
);
const ExpiredTokenPage = lazy(() => import('~/pages/expired-token'));
const ExternalDocumentViewerPage = lazy(
  () => import('~/pages/external-document-viewer'),
);
const Fields = lazy(() => import('~/pages/fields'));
const FieldModel = lazy(() => import('~/pages/fields/[modelId]'));
const SelectDebug = lazy(() => import('~/pages/fields/debug'));
const DebugModel = lazy(() => import('~/pages/fields/debug/[modelId]'));
const PermissionFromSSOPage = lazy(
  () => import('~/pages/folders-permission-check'),
);
const ForgotPasswordPage = lazy(() => import('~/pages/forgot-password'));
const GuidelinesRoot = lazy(() => import('~/pages/guidelines'));
const GuidelinesListPage = lazy(() =>
  import('~/features/guidelines/GuidelinesListPage').then((m) => ({
    default: m.GuidelinesListPage,
  })),
);
const GuidelineDetailsPage = lazy(() =>
  import('~/features/guidelines/GuidelineDetailsPage').then((m) => ({
    default: m.GuidelineDetailsPage,
  })),
);
const HomePage = lazy(() => import('~/pages/home'));
const TicketEmailActionError = lazy(
  () => import('~/pages/invalid-email-action-token'),
);
const LoginPage = lazy(() => import('~/pages/login'));
const LoginEmbedPage = lazy(() => import('~/pages/login/embed'));
const LoginEmbedSuccessPage = lazy(() => import('~/pages/login/embed/success'));
const ManagedEntityPartiesPage = lazy(
  () => import('~/pages/managed-entity-parties'),
);
const Onboarding = lazy(() => import('~/pages/onboarding'));
const PlaybooksRoot = lazy(() => import('~/pages/playbooks'));
const PlaybooksListPage = lazy(() =>
  import('~/features/playbooks/PlaybooksListPage').then((m) => ({
    default: m.PlaybooksListPage,
  })),
);
const PlaybookDetailsPage = lazy(() =>
  import('~/features/playbooks/PlaybookDetailsPage').then((m) => ({
    default: m.PlaybookDetailsPage,
  })),
);
const UnifiedFieldsRegistryPlaygroundPage = lazy(
  () => import('~/pages/playground/unified-fields-registry'),
);
const RBACPage = lazy(() => import('~/pages/rbac'));
const SavedDashboards = lazy(() => import('~/pages/saved-dashboards'));
const SavedSearches = lazy(() => import('~/pages/saved-searches'));
const SearchV2 = lazy(() => import('~/pages/search'));
const SettingsPage = lazy(() => import('~/pages/settings'));
const SuperAdminUsersPage = lazy(() => import('~/pages/super-admin/users'));
const TicketEmailApproveComment = lazy(
  () =>
    import('~/pages/tickets/[ticketId]/judgment-results/[judgementResultId]/[jwt]/approve'),
);
const TicketEmailRejectComment = lazy(
  () =>
    import('~/pages/tickets/[ticketId]/judgment-results/[judgementResultId]/[jwt]/reject'),
);
const Unauthorized = lazy(() => import('~/pages/unauthorized'));
const UploadPage = lazy(() => import('~/pages/upload'));
const WordOnlineErrorPage = lazy(
  () => import('~/pages/word-online-invalid-subscription'),
);
const WordOnlinePage = lazy(() => import('~/pages/word-online/[file-id]'));
const WizardTicketForm = lazy(
  () => import('~/pages/workflow/[workflowId]/intake-form/link'),
);
const WssIntakeFormRedirect = lazy(
  () => import('~/pages/integrate-workflow/[workflowId]/intake-form/link'),
);
const WorkflowBuilderDashboardPage = lazy(
  () => import('~/pages/workflow/builder'),
);
const WorkflowBuilderPage = lazy(
  () => import('~/pages/workflow/builder/[workflowId]'),
);
const WorkflowTicketsPage = lazy(() => import('~/pages/workflow/tickets'));
const TicketsViewPage = lazy(
  () => import('~/pages/workflow/tickets/[ticketId]'),
);
const TicketReviewerPage = lazy(
  () => import('~/pages/workflow/tickets/[ticketId]/reviewer/[versionNumber]'),
);
const WorkflowTicketsNewPage = lazy(
  () => import('~/pages/workflow/tickets/new'),
);
const WorkflowCLMEvolutionRequestDetailPage = lazy(
  () => import('~/pages/workflows-clm-evolution/requests/[ticketId]'),
);
const WorkflowCLMEvolutionEsignRedirectPage = lazy(
  () => import('~/pages/workflows-clm-evolution/esign-redirect'),
);
const WorkflowCLMEvolutionTicketsPage = lazy(
  () => import('~/pages/workflows-clm-evolution/tickets'),
);
const WorkflowCLMEvolutionNewRequestPage = lazy(
  () => import('~/pages/workflows-clm-evolution/tickets/new'),
);
const WorkflowCLMEvolutionWorkflowsPage = lazy(
  () => import('~/pages/workflows-clm-evolution/workflows'),
);
const WorkflowCLMEvolutionWorkflowDetailPage = lazy(
  () => import('~/pages/workflows-clm-evolution/workflows/[workflowId]'),
);
const XRayLibrary = lazy(() => import('~/pages/x-ray/library'));
const XRayLibraryAdmin = lazy(() => import('~/pages/x-ray/library/admin'));

const adminConsoleCrumb = defineMessage({
  id: 'Nav.adminConsole',
  defaultMessage: 'Admin Console',
  description: 'Navigation text for Admin Console',
});

/**
 * Every admin console page hangs directly off the console. Unlike the inline
 * `generateNav` trail this replaces, the crumb links through the route's own
 * `:clientId` rather than the signed-in user's — an admin viewing another
 * client saw a console link back to their own.
 */
const adminConsoleTrail: BreadcrumbCrumb[] = [
  { title: adminConsoleCrumb, path: RoutePathType.AdminConsoleClient },
];

/**
 * One trail per top-level sidebar nav group, so a page's crumbs start where the
 * nav does. Each crumb links to the group's own landing destination — the same
 * `pathname` its nav entry uses (see `createSidebarNavlinks` in
 * `~/utils/navLinks`). A group whose landing page is also its first child (Custom
 * AI → Fields, Workflows → Requests) repeats that link in the second crumb,
 * matching what the nav itself shows.
 */
const workflowTrail: BreadcrumbCrumb[] = [
  {
    title: defineMessage({
      id: 'PageHeader.navLinks.workflow',
      defaultMessage: 'Workflows',
      description: 'Workflow navigation link',
    }),
    path: RoutePathType.WorkflowTickets,
  },
];

const workflowsClmEvolutionTrail: BreadcrumbCrumb[] = [
  {
    title: defineMessage({
      id: 'PageHeader.navLinks.workflowsClmEvolution',
      defaultMessage: 'Workflows',
      description: 'Workflows CLM Evolution navigation link',
    }),
    path: RoutePathType.WorkflowCLMEvolutionRequests,
  },
];

const dashboardsTrail: BreadcrumbCrumb[] = [
  {
    title: defineMessage({
      id: 'PageHeader.navLinks.dashboards',
      defaultMessage: 'Dashboards',
      description: 'Dashboards navigation link',
    }),
    path: RoutePathType.Dashboards,
  },
];

const searchTrail: BreadcrumbCrumb[] = [
  {
    title: defineMessage({
      id: 'PageHeader.navLinks.search',
      defaultMessage: 'Search',
      description: 'Search navigation link',
    }),
    path: RoutePathType.Search,
  },
];

const libraryTrail: BreadcrumbCrumb[] = [
  {
    title: defineMessage({
      id: 'PageHeader.navLinks.clauseLibrary',
      defaultMessage: 'Library',
      description: 'Clause Library navigation link',
    }),
    path: RoutePathType.Playbooks,
  },
];

const customAiCrumb = defineMessage({
  id: 'Routes.customAiCrumb',
  defaultMessage: 'Custom AI',
  description: 'Breadcrumb text for the Custom AI fields page',
});

const customAiTrail: BreadcrumbCrumb[] = [
  { title: customAiCrumb, path: RoutePathType.AutomationHubFields },
];

/**
 * Shared by the workflow detail route and the child routes rendered inside it,
 * which are the same page as far as the breadcrumb is concerned. Every route in
 * a nested group has to declare its own trail — the deepest match wins, so a
 * child that declared nothing would drop the ancestors its parent named.
 */
const workflowClmEvolutionDetailTrail: BreadcrumbCrumb[] = [
  ...workflowsClmEvolutionTrail,
  {
    title: defineMessage({
      id: 'Routes.workflowCLMEvolutionWorkflowsTitle',
      defaultMessage: 'Workflow Builder',
      description: 'Title for the workflow builder page route',
    }),
    path: RoutePathType.WorkflowCLMEvolutionWorkflows,
  },
  { resolver: useWorkflowNameCrumb },
];

/**
 * A private route, which renders inside `LoggedInLayout` and therefore under the
 * `AppBreadcrumb` bar. `handle` is required and typed, so every page must state
 * its `title` and its `breadcrumbs` — see `RouteHandle`. Public routes stay on
 * the plain `RouteObject`: they render under `LoggedOutLayout`, which has no
 * breadcrumb bar for a trail to appear in.
 */
type PrivateRouteObject = RouteObject & {
  handle: RouteHandle;
  children?: PrivateRouteObject[];
};

const privateRouteList: PrivateRouteObject[] = [
  {
    path: RoutePathType.Home,
    element: (
      <RouteGate
        path={RoutePathType.Home}
        resolvers={[
          {
            permission: canUseHome,
            component: HomePage,
          },
          {
            permission: checkIsRbac,
            component: RBACPage,
          },
          {
            permission: checkIsWorkflowOnly,
            redirectPath: RoutePathType.WorkflowTickets,
          },
          {
            permission: canUseSearchAsHomePage,
            redirectPath: RoutePathType.Search,
          },
          {
            redirectPath: RoutePathType.Documents,
          },
        ]}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.homeTitle',
        defaultMessage: 'Home',
        description: 'Title for the home page route',
      }),
      // The landing page names itself in the nav and in its own content; a
      // one-crumb trail reading "Home" above it is noise.
      breadcrumbs: 'none',
    },
  },
  {
    path: RoutePathType.HomeSurface,
    element: (
      <RouteGate
        path={RoutePathType.HomeSurface}
        flag={FlagType.Home}
        resolvers={[{ component: HomePage }]}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.homeSurfaceTitle',
        defaultMessage: 'Home',
        description: 'Title for the home surface page route',
      }),
      breadcrumbs: 'none',
    },
  },
  {
    path: RoutePathType.Admin,
    element: (
      <RouteGate
        path={RoutePathType.Admin}
        resolvers={createAdminResolvers(ClientsPage)}
      />
    ),
    handle: {
      // Names the page it renders — the clients table — not the console it links
      // to. Reuses the page's own message so the tab title, the crumb and the
      // heading cannot drift.
      title: defineMessage({
        id: 'AdminPage.pageTitle',
        defaultMessage: 'Manage Clients',
        description: 'Page title for admin clients management',
      }),
      // No ancestor: picking a client here is what leads *to* a console, so the
      // inline trail this replaced had the hierarchy backwards.
      breadcrumbs: 'self',
    },
  },
  {
    path: RoutePathType.AdminConsole,
    element: (
      <RouteGate
        path={RoutePathType.AdminConsole}
        resolvers={createAdminResolvers(AdminNavsPage)}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.adminConsoleTitle',
        defaultMessage: 'Admin Console',
        description: 'Title for the admin console page route',
      }),
      breadcrumbs: 'self',
    },
  },
  {
    path: RoutePathType.AdminConsoleClient,
    element: (
      <RouteGate
        path={RoutePathType.AdminConsoleClient}
        resolvers={createAdminResolvers(AdminNavsPage)}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.adminConsoleTitle',
        defaultMessage: 'Admin Console',
        description: 'Title for the admin console page route',
      }),
      breadcrumbs: 'self',
    },
  },
  {
    path: RoutePathType.AdminConsoleClientAuditLogs,
    element: (
      <RouteGate
        path={RoutePathType.AdminConsoleClientAuditLogs}
        permissions={[
          {
            resourceId: 'audit_logs',
            resourceType: 'view',
          },
        ]}
        resolvers={createAdminResolvers(AuditLogsPage)}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.auditLogsTitle',
        defaultMessage: 'Audit Logs',
        description: 'Title for the audit logs page route',
      }),
      breadcrumbs: adminConsoleTrail,
    },
  },
  {
    path: RoutePathType.AdminConsoleClientCopyWorkflow,
    element: (
      <RouteGate
        path={RoutePathType.AdminConsoleClientCopyWorkflow}
        resolvers={createSuperAdminResolvers(CopyWorkflowPage)}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.copyWorkflowTitle',
        defaultMessage: 'Copy Workflow',
        description: 'Title for the copy workflow page route',
      }),
      breadcrumbs: adminConsoleTrail,
    },
  },
  {
    path: RoutePathType.AdminConsoleClientDataFields,
    element: (
      <RouteGate
        path={RoutePathType.AdminConsoleClientDataFields}
        permissions={[
          {
            resourceId: 'data_fields',
            resourceType: 'view',
          },
        ]}
        resolvers={[
          {
            permission: (user: User) =>
              !getFlag(FlagType.UnifiedFieldsM1) && checkIsAdmin(user),
            component: DataFieldsPage,
          },
          {
            redirectPath: RoutePathType.Home,
          },
        ]}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.dataFieldsTitle',
        defaultMessage: 'Data Fields',
        description: 'Title for the data fields page route',
      }),
      breadcrumbs: adminConsoleTrail,
    },
  },
  {
    path: RoutePathType.AdminConsoleClientFieldManager,
    element: (
      <RouteGate
        path={RoutePathType.AdminConsoleClientFieldManager}
        flag={FlagType.UnifiedFieldsM1}
        permissions={[
          {
            resourceId: 'field_manager',
            resourceType: 'view',
          },
        ]}
        resolvers={createAdminResolvers(FieldManagerPage)}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.fieldManagerTitle',
        defaultMessage: 'Field Manager',
        description: 'Title for the field manager page route',
      }),
      breadcrumbs: adminConsoleTrail,
    },
  },
  {
    path: RoutePathType.AdminConsoleClientEntityTypes,
    element: (
      <RouteGate
        path={RoutePathType.AdminConsoleClientEntityTypes}
        flag={FlagType.MesPartiesMvp}
        permissions={[{ resourceId: 'entity_types', resourceType: 'view' }]}
        resolvers={createAdminResolvers(EntityTypesPage)}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.entityTypesTitle',
        defaultMessage: 'Entity Types',
        description: 'Title for the entity types page route',
      }),
      breadcrumbs: adminConsoleTrail,
    },
  },
  {
    path: RoutePathType.AdminConsoleClientEntityTypeDetail,
    element: (
      <RouteGate
        path={RoutePathType.AdminConsoleClientEntityTypeDetail}
        flag={FlagType.MesPartiesMvp}
        permissions={[{ resourceId: 'entity_types', resourceType: 'view' }]}
        resolvers={createAdminResolvers(EntityTypeDetailPage)}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.entityTypeDetailTitle',
        defaultMessage: 'Entity Type',
        description: 'Title for the entity type detail page route',
      }),
      breadcrumbs: [
        ...adminConsoleTrail,
        {
          title: defineMessage({
            id: 'Routes.entityTypesTitle',
            defaultMessage: 'Entity Types',
            description: 'Title for the entity types page route',
          }),
          path: RoutePathType.AdminConsoleClientEntityTypes,
        },
        { resolver: useEntityTypeNameCrumb },
      ],
    },
  },
  {
    path: RoutePathType.AdminConsoleClientDepartments,
    element: (
      <RouteGate
        path={RoutePathType.AdminConsoleClientDepartments}
        permissions={[
          {
            resourceId: 'departments',
            resourceType: 'view',
          },
        ]}
        resolvers={createAdminResolvers(DepartmentsPage)}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.departmentsTitle',
        defaultMessage: 'Departments',
        description: 'Title for the departments page route',
      }),
      breadcrumbs: adminConsoleTrail,
    },
  },
  {
    path: RoutePathType.AdminConsoleClientDepartmentsDepartment,
    element: (
      <RouteGate
        path={RoutePathType.AdminConsoleClientDepartmentsDepartment}
        permissions={[
          {
            resourceId: 'departments',
            resourceType: 'view',
          },
        ]}
        resolvers={createAdminResolvers(DepartmentPage)}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.departmentDetailsTitle',
        defaultMessage: 'Department Details',
        description: 'Title for the department details page route',
      }),
      // TODO: declare this trail once pilot `GET /api/v2/department/detail/:id/`
      // moves to RTK Query. Deferred only because the department name is fetched
      // imperatively into class state, where no resolver can reach it.
      breadcrumbs: 'deferred',
    },
  },
  {
    path: RoutePathType.AdminConsoleClientEmailIntake,
    element: (
      <RouteGate
        path={RoutePathType.AdminConsoleClientEmailIntake}
        permissions={[
          {
            resourceId: 'email_intake',
            resourceType: 'view',
          },
        ]}
        resolvers={[
          {
            permission: checkIsAdmin,
            component: EmailIntakePage,
          },
          {
            redirectPath: RoutePathType.AdminConsoleClient,
          },
        ]}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.emailIntakeTitle',
        defaultMessage: 'Email Intake',
        description: 'Title for the email intake page route',
      }),
      breadcrumbs: adminConsoleTrail,
    },
  },
  {
    path: RoutePathType.AdminConsoleClientProcurement,
    element: (
      <RouteGate
        path={RoutePathType.AdminConsoleClientProcurement}
        permissions={[
          {
            resourceId: 'integrations',
            resourceType: 'view',
          },
        ]}
        resolvers={createAdminResolvers(ProcurementPage)}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.procurementTitle',
        defaultMessage: 'Procurement',
        description: 'Title for the procurement page route',
      }),
      breadcrumbs: adminConsoleTrail,
    },
  },
  {
    path: RoutePathType.AdminConsoleClientIntegrations,
    element: (
      <RouteGate
        path={RoutePathType.AdminConsoleClientIntegrations}
        permissions={[
          {
            resourceId: 'integrations',
            resourceType: 'view',
          },
        ]}
        resolvers={createAdminResolvers(IntegrationsPage)}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.integrationsTitle',
        defaultMessage: 'Integrations',
        description: 'Title for the integrations page route',
      }),
      breadcrumbs: adminConsoleTrail,
    },
  },
  {
    path: RoutePathType.AdminConsoleClientManagePartyLists,
    element: (
      <RouteGate
        path={RoutePathType.AdminConsoleClientManagePartyLists}
        resolvers={createAdminResolvers(PartyListsPage)}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.managePartyListsTitle',
        defaultMessage: 'Manage Party Lists',
        description: 'Title for the manage party lists page route',
      }),
      breadcrumbs: adminConsoleTrail,
    },
  },
  {
    path: RoutePathType.AdminConsoleClientMoreSettings,
    element: (
      <RouteGate
        path={RoutePathType.AdminConsoleClientMoreSettings}
        permissions={[
          {
            resourceId: 'root_folder_access_settings',
            resourceType: 'view',
          },
          {
            resourceId: 'documents_metadata_bulk_upload',
            resourceType: 'view',
          },
          {
            resourceId: 'clause_samples',
            resourceType: 'view',
          },
          {
            resourceId: 'deleted_documents',
            resourceType: 'view',
          },
        ]}
        resolvers={createAdminResolvers(MoreSettingsPage)}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.moreSettingsTitle',
        defaultMessage: 'More Settings',
        description: 'Title for the more settings page route',
      }),
      breadcrumbs: adminConsoleTrail,
    },
  },
  {
    path: RoutePathType.AdminConsoleClientProcessDocuments,
    element: (
      <RouteGate
        path={RoutePathType.AdminConsoleClientProcessDocuments}
        resolvers={createSuperAdminResolvers(DocumentProcessingPage)}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.processDocumentsTitle',
        defaultMessage: 'Process Documents',
        description: 'Title for the process documents page route',
      }),
      // Explicit leaf: `title` reads "Process Documents"; keep the page's wording.
      breadcrumbs: [
        ...adminConsoleTrail,
        {
          title: defineMessage({
            id: 'ProcessDocuments.navTitle',
            defaultMessage: 'Document Processing',
            description: 'Navigation title for document processing page',
          }),
        },
      ],
    },
  },
  {
    path: RoutePathType.AdminConsoleClientRestorationHub,
    element: (
      <RouteGate
        path={RoutePathType.AdminConsoleClientRestorationHub}
        permissions={[
          {
            resourceId: 'restoration_hub',
            resourceType: 'view',
          },
        ]}
        resolvers={[
          {
            permission: canUseRestorationHub,
            component: DocumentsRestore,
          },
          {
            redirectPath: RoutePathType.Home,
          },
        ]}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.restorationHubTitle',
        defaultMessage: 'Restoration Hub',
        description: 'Title for the restoration hub page route',
      }),
      breadcrumbs: adminConsoleTrail,
    },
  },
  {
    path: RoutePathType.AdminConsoleClientSamlConfiguration,
    element: (
      <RouteGate
        path={RoutePathType.AdminConsoleClientSamlConfiguration}
        permissions={[
          {
            resourceId: 'sso',
            resourceType: 'view',
          },
        ]}
        resolvers={createAdminResolvers(SSOConfigPage)}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.samlConfigurationTitle',
        defaultMessage: 'SAML Configuration',
        description: 'Title for the SAML configuration page route',
      }),
      // Explicit leaf: `title` reads "SAML Configuration"; keep the page's wording.
      breadcrumbs: [
        ...adminConsoleTrail,
        {
          title: defineMessage({
            id: 'SamlConfiguration.navTitle',
            defaultMessage: 'Single Sign-On (SAML)',
            description: 'Navigation title for SAML configuration page',
          }),
        },
      ],
    },
  },
  {
    path: RoutePathType.AdminConsoleClientSamlConfigurationFormConfig,
    element: (
      <RouteGate
        path={RoutePathType.AdminConsoleClientSamlConfigurationFormConfig}
        permissions={[
          {
            resourceId: 'sso',
            resourceType: 'view',
          },
        ]}
        resolvers={createAdminResolvers(SAMLConfigPage)}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.samlConfigurationFormTitle',
        defaultMessage: 'SAML Configuration',
        description: 'Title for the SAML configuration form page route',
      }),
      breadcrumbs: [
        ...adminConsoleTrail,
        {
          title: defineMessage({
            id: 'Routes.samlConfigurationTitle',
            defaultMessage: 'SAML Configuration',
            description: 'Title for the SAML configuration page route',
          }),
          path: RoutePathType.AdminConsoleClientSamlConfiguration,
        },
      ],
    },
  },
  {
    path: RoutePathType.AdminConsoleClientDocumentManagement,
    element: (
      <RouteGate
        path={RoutePathType.AdminConsoleClientDocumentManagement}
        resolvers={createAdminResolvers(DocumentManagementPage)}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.adminConsoleDocumentManagementTitle',
        defaultMessage: 'Document Management',
        description:
          'Title for the admin console document management page route',
      }),
      breadcrumbs: adminConsoleTrail,
    },
  },
  {
    path: RoutePathType.AdminConsoleClientCRM,
    element: (
      <RouteGate
        path={RoutePathType.AdminConsoleClientCRM}
        permissions={[
          {
            resourceId: 'integrations',
            resourceType: 'view',
          },
        ]}
        resolvers={createAdminResolvers(CRMPage)}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.adminConsoleCRMTitle',
        defaultMessage: 'CRM',
        description: 'Title for the admin console crm page route',
      }),
      breadcrumbs: adminConsoleTrail,
    },
  },
  {
    path: RoutePathType.AdminConsoleClientApiManagement,
    element: (
      <RouteGate
        path={RoutePathType.AdminConsoleClientApiManagement}
        permissions={[
          {
            resourceId: 'integrations',
            resourceType: 'view',
          },
        ]}
        resolvers={createAdminResolvers(ApiManagementPage)}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.adminConsoleApiManagementTitle',
        defaultMessage: 'API Management',
        description: 'Title for the admin console api management page route',
      }),
      breadcrumbs: adminConsoleTrail,
    },
  },
  {
    path: RoutePathType.AdminConsoleClientWebhooks,
    element: (
      <RouteGate
        path={RoutePathType.AdminConsoleClientWebhooks}
        permissions={[
          {
            resourceId: 'integrations',
            resourceType: 'view',
          },
        ]}
        resolvers={createAdminResolvers(WebhooksPage)}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.adminConsoleWebhooksTitle',
        defaultMessage: 'Webhooks',
        description: 'Title for the admin console webhooks page route',
      }),
      breadcrumbs: adminConsoleTrail,
    },
  },
  {
    path: RoutePathType.AdminClientCredits,
    element: (
      <RouteGate
        path={RoutePathType.AdminClientCredits}
        resolvers={createAdminResolvers(AdminCreditsPage)}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.creditsTitle',
        defaultMessage: 'Credits',
        description: 'Title for the credits page route',
      }),
      breadcrumbs: [
        {
          title: defineMessage({
            id: 'Routes.adminTitle',
            defaultMessage: 'Admin',
            description: 'Title for the admin page route',
          }),
          path: RoutePathType.Admin,
        },
        {
          title: defineMessage({
            id: 'Routes.adminConsoleTitle',
            defaultMessage: 'Admin Console',
            description: 'Title for the admin console page route',
          }),
          path: RoutePathType.AdminConsoleClient,
        },
        { resolver: useAdminClientNameCrumb },
      ],
    },
  },
  {
    path: RoutePathType.AdminRoles,
    element: (
      <RouteGate
        path={RoutePathType.AdminRoles}
        permissions={[
          {
            resourceId: 'roles',
            resourceType: 'view',
          },
        ]}
        resolvers={createDefaultResolvers(AdminRolesPage)}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.rolesTitle',
        defaultMessage: 'Roles',
        description: 'Title for the roles page route',
      }),
      breadcrumbs: adminConsoleTrail,
    },
  },
  {
    path: RoutePathType.AdminUsers,
    element: (
      <RouteGate
        path={RoutePathType.AdminUsers}
        permissions={[
          {
            resourceId: 'users',
            resourceType: 'view',
          },
        ]}
        resolvers={[
          {
            permission: canUseUsersPage,
            component: AdminUsersPage,
          },
          {
            redirectPath: RoutePathType.Home,
          },
        ]}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.usersTitle',
        defaultMessage: 'Users',
        description: 'Title for the users page route',
      }),
      breadcrumbs: adminConsoleTrail,
    },
  },
  {
    path: RoutePathType.Alerts,
    element: (
      <RouteGate
        path={RoutePathType.Alerts}
        permissions={[
          {
            resourceId: 'documents',
            resourceType: 'view',
          },
        ]}
        resolvers={[
          {
            permission: checkIsWorkflowOnly,
            redirectPath: RoutePathType.Home,
          },
          {
            // Alerts were hidden under Unified Fields M1 and are back for M2.
            // M2 implies M1, so carve M2 out to keep the redirect for M1-only.
            permission: () =>
              getFlag(FlagType.UnifiedFieldsM1) &&
              !getFlag(FlagType.UnifiedFieldsM2),
            redirectPath: RoutePathType.Home,
          },
          {
            permission: () => getFlag(FlagType.AlertsListV2),
            component: AlertsPageV2,
          },
          {
            component: AlertsPage,
          },
        ]}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.alertsTitle',
        defaultMessage: 'Alerts',
        description: 'Title for the alerts page route',
      }),
      breadcrumbs: searchTrail,
    },
  },
  {
    path: RoutePathType.ClauseLibrary,
    element: (
      <RouteGate
        path={RoutePathType.ClauseLibrary}
        flag={FlagType.ClauseLibrary}
        permissions={[
          {
            resourceId: 'clause_library',
            resourceType: 'view',
          },
        ]}
        resolvers={[
          {
            /* TODO: Add isClauseLibraryAdmin permission check */
            //permission: canUseClauseLibrary,
            component: ClauseLibrary,
          },
          {
            redirectPath: RoutePathType.Home,
          },
        ]}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.clauseLibraryTitle',
        defaultMessage: 'Clause Library',
        description: 'Title for the clause library page route',
      }),
      breadcrumbs: libraryTrail,
    },
    children: [
      {
        path: RoutePathType.ClauseLibraryInstance,
        element: (
          <Suspense fallback={null}>
            <ClauseView />
          </Suspense>
        ),
        handle: {
          title: defineMessage({
            id: 'Routes.clauseLibraryTitle',
            defaultMessage: 'Clause Library',
            description: 'Title for the clause library page route',
          }),
          breadcrumbs: libraryTrail,
        },
      },
    ],
  },
  {
    path: RoutePathType.Dashboards,
    element: (
      <RouteGate
        path={RoutePathType.Dashboards}
        permissions={[
          {
            resourceId: 'workflows_dashboard',
            resourceType: 'view',
          },
          {
            resourceId: 'documents_dashboard',
            resourceType: 'view',
          },
        ]}
        resolvers={createDefaultResolvers(DashboardsPage)}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.dashboardsTitle',
        defaultMessage: 'Dashboards',
        description: 'Title for the dashboards page route',
      }),
      breadcrumbs: 'self',
    },
  },
  {
    path: RoutePathType.Dashboard,
    element: (
      <RouteGate
        path={RoutePathType.Dashboard}
        permissions={[
          {
            resourceId: 'workflows_dashboard',
            resourceType: 'view',
          },
          {
            resourceId: 'documents_dashboard',
            resourceType: 'view',
          },
        ]}
        resolvers={createDefaultResolvers(DashboardsPage)}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.dashboardsTitle',
        defaultMessage: 'Dashboards',
        description: 'Title for the dashboards page route',
      }),
      breadcrumbs: [...dashboardsTrail, { resolver: useDashboardNameCrumb }],
    },
  },
  {
    path: RoutePathType.AutomationHub,
    element: (
      <RouteGate
        path={RoutePathType.AutomationHub}
        permissions={[
          {
            resourceId: 'clause_samples',
            resourceType: 'view',
          },
        ]}
        resolvers={[
          {
            permission: canUseAutomation,
            component: AutomationHubPage,
          },
          {
            permission: canUseWorkflowAdmin,
            redirectPath: RoutePathType.RoutingTable,
          },
          {
            redirectPath: RoutePathType.Home,
          },
        ]}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'PageHeader.subNavLinks.automation',
        defaultMessage: 'Clauses',
        description: 'Clause automation navigation link',
      }),
      breadcrumbs: customAiTrail,
    },
  },
  {
    path: RoutePathType.AutomationHubFields,
    element: (
      <RouteGate
        path={RoutePathType.AutomationHubFields}
        resolvers={[
          {
            permission: canUseAutomation,
            component: Fields,
          },
          {
            permission: canUseWorkflowAdmin,
            redirectPath: RoutePathType.RoutingTable,
          },
          {
            redirectPath: RoutePathType.Home,
          },
        ]}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'PageHeader.subNavLinks.fields',
        defaultMessage: 'Fields',
        description: 'Fields automation navigation link',
      }),
      breadcrumbs: customAiTrail,
    },
  },
  {
    path: RoutePathType.AutomationHubFieldModel,
    element: (
      <RouteGate
        path={RoutePathType.AutomationHubFieldModel}
        resolvers={[
          {
            permission: canUseAutomation,
            component: FieldModel,
          },
          {
            redirectPath: RoutePathType.Home,
          },
        ]}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.automationFieldModelTitle',
        defaultMessage: 'Automation',
        description: 'Title for the automation field model page route',
      }),
      breadcrumbs: [...customAiTrail, { resolver: useFieldModelNameCrumb }],
    },
  },
  {
    path: RoutePathType.AutomationHubFieldsDebug,
    element: (
      <RouteGate
        path={RoutePathType.AutomationHubFieldsDebug}
        flag={FlagType.CustomAiModelDebug}
        resolvers={[
          {
            permission: canUseAutomation,
            component: SelectDebug,
          },
          {
            redirectPath: RoutePathType.Home,
          },
        ]}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.automationFieldModelDebugTitle',
        defaultMessage: 'Custom AI Model Debug',
        description: 'Title for the Custom AI model debug page route',
      }),
      breadcrumbs: customAiTrail,
    },
  },
  {
    path: RoutePathType.AutomationHubFieldsDebugModel,
    element: (
      <RouteGate
        path={RoutePathType.AutomationHubFieldsDebugModel}
        flag={FlagType.CustomAiModelDebug}
        resolvers={[
          {
            permission: canUseAutomation,
            component: DebugModel,
          },
          {
            redirectPath: RoutePathType.Home,
          },
        ]}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.automationFieldModelDebugTitle',
        defaultMessage: 'Custom AI Model Debug',
        description: 'Title for the Custom AI model debug page route',
      }),
      breadcrumbs: [
        ...customAiTrail,
        {
          title: defineMessage({
            id: 'Routes.automationFieldsDebugCrumb',
            defaultMessage: 'Debug',
            description: 'Breadcrumb text for the Custom AI debug page',
          }),
          path: RoutePathType.AutomationHubFieldsDebug,
        },
        {
          title: defineMessage({
            id: 'Routes.automationFieldModelDebugCrumb',
            defaultMessage: 'Debug Model',
            description: 'Breadcrumb text for the Custom AI model debug page',
          }),
        },
      ],
    },
  },
  {
    path: RoutePathType.AutomationHubDetail,
    element: (
      <RouteGate
        path={RoutePathType.AutomationHubDetail}
        permissions={[
          {
            resourceId: 'clause_samples',
            resourceType: 'view',
          },
        ]}
        resolvers={[
          {
            permission: canUseAutomation,
            component: AutomationHubDetailPage,
          },
          {
            redirectPath: RoutePathType.Home,
          },
        ]}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.automationDetailTitle',
        defaultMessage: 'Automation',
        description: 'Title for the automation detail page route',
      }),
      // One crumb under the section, like the other Custom AI tabs: the clause
      // when the page carries one, the tab name when it does not. A tab crumb
      // between the two would repeat whichever of them is showing.
      breadcrumbs: [...customAiTrail, { resolver: useAutomationClauseCrumb }],
    },
  },
  {
    path: RoutePathType.RoutingTable,
    element: (
      <RouteGate
        path={RoutePathType.RoutingTable}
        permissions={[
          {
            resourceId: 'workflow_templates',
            resourceType: 'view',
          },
        ]}
        resolvers={[
          {
            permission: checkIsWorkflowOnly,
            redirectPath: RoutePathType.WorkflowTickets,
          },
          {
            component: RoutingTablePage,
          },
        ]}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.routingTableTitle',
        defaultMessage: 'Routing Table',
        description: 'Title for the routing table page route',
      }),
      // Routing Table moved from the Workflows sub-nav to Custom AI — its
      // breadcrumb trail follows.
      breadcrumbs: customAiTrail,
    },
  },
  {
    path: RoutePathType.DynamicDataExtraction,
    element: (
      <RouteGate
        path={RoutePathType.DynamicDataExtraction}
        flag={FlagType.DynamicDataExtraction}
        permissions={[
          {
            resourceId: 'workflow_templates',
            resourceType: 'view',
          },
        ]}
        resolvers={[
          {
            permission: checkIsWorkflowOnly,
            redirectPath: RoutePathType.WorkflowTickets,
          },
          {
            component: DynamicDataExtractionPage,
          },
        ]}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.dataExtractionTitle',
        defaultMessage: 'Data Extraction Table',
        description: 'Title for the data extraction table page route',
      }),
      breadcrumbs: 'self',
    },
  },
  {
    path: RoutePathType.ViewDataExtraction,
    element: (
      <RouteGate
        path={RoutePathType.ViewDataExtraction}
        flag={FlagType.DynamicDataExtraction}
        permissions={[
          {
            resourceId: 'workflow_templates',
            resourceType: 'view',
          },
        ]}
        resolvers={[
          {
            permission: checkIsWorkflowOnly,
            redirectPath: RoutePathType.WorkflowTickets,
          },
          {
            component: ViewDataExtractionPage,
          },
        ]}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.viewDataExtractionTitle',
        defaultMessage: 'Dynamic Data Extraction',
        description: 'Title for the view data extraction page route',
      }),
      breadcrumbs: [
        {
          title: defineMessage({
            id: 'Routes.dataExtractionTitle',
            defaultMessage: 'Data Extraction Table',
            description: 'Title for the data extraction table page route',
          }),
          path: RoutePathType.DynamicDataExtraction,
        },
        { resolver: useDataExtractionNameCrumb },
      ],
    },
  },
  {
    path: RoutePathType.AgentForgeInvoke,
    element: (
      <RouteGate
        path={RoutePathType.AgentForgeInvoke}
        flag={FlagType.DevOnlyAgentForgeInvoke}
        resolvers={[
          {
            component: AgentForgeInvokePage,
          },
          {
            redirectPath: RoutePathType.Home,
          },
        ]}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.agentForgeInvokeTitle',
        defaultMessage: 'Agent Forge Invoke',
        description: 'Title for the agent forge invoke page route',
      }),
      breadcrumbs: 'self',
    },
  },
  {
    path: RoutePathType.PlaygroundUnifiedFieldsRegistry,
    element: (
      <RouteGate
        path={RoutePathType.PlaygroundUnifiedFieldsRegistry}
        flag={FlagType.DevOnlyUnifiedFieldsRegistryPlayground}
        resolvers={[
          {
            component: UnifiedFieldsRegistryPlaygroundPage,
          },
          {
            redirectPath: RoutePathType.Home,
          },
        ]}
      />
    ),
    handle: {
      // Dev-only page — hardcoded on purpose, not run through defineMessage,
      // so it never lands in the i18n extraction bundle.
      title: {
        id: 'Routes.unifiedFieldsRegistryPlaygroundTitle',
        defaultMessage: 'Unified Fields Registry Playground',
      },
      breadcrumbs: 'self',
    },
  },
  {
    path: RoutePathType.Onboarding,
    element: (
      <RouteGate
        path={RoutePathType.Onboarding}
        flag={FlagType.CanduScript}
        resolvers={[
          {
            component: Onboarding,
          },
          {
            redirectPath: RoutePathType.Home,
          },
        ]}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.onboardingTitle',
        defaultMessage: 'Onboarding',
        description: 'Title for the onboarding page route',
      }),
      breadcrumbs: 'self',
    },
  },
  {
    path: RoutePathType.Documents,
    element: (
      <RouteGate
        path={RoutePathType.Documents}
        permissions={[
          {
            resourceId: 'documents',
            resourceType: 'view',
          },
        ]}
        resolvers={createDefaultResolvers(DocumentsPage)}
      />
    ),
    handle: {
      // Deferred: the folder path is arbitrarily deep and lives in page state,
      // so the page portals its own trail into the breadcrumb bar. See
      // `RouteBreadcrumbs`.
      breadcrumbs: 'deferred',
      title: defineMessage({
        id: 'Routes.documentsTitle',
        defaultMessage: 'Documents',
        description: 'Title for the documents page route',
      }),
    },
  },
  {
    path: RoutePathType.DocumentsEntityType,
    element: (
      <RouteGate
        path={RoutePathType.DocumentsEntityType}
        permissions={[
          { resourceId: 'entity_groups', resourceType: 'view' },
          { resourceId: 'entities', resourceType: 'view' },
          { resourceId: 'entity_types', resourceType: 'view' },
        ]}
        resolvers={createDefaultResolvers(DocumentsEntityTypePage)}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.documentsEntityTypeTitle',
        defaultMessage: 'Documents',
        description: 'Title for the documents entity type page route',
      }),
      breadcrumbs: [
        {
          title: defineMessage({
            id: 'Routes.documentsTitle',
            defaultMessage: 'Documents',
            description: 'Title for the documents page route',
          }),
          path: RoutePathType.Documents,
        },
        { resolver: useEntityTypeNameCrumb },
      ],
    },
  },
  {
    path: RoutePathType.DocumentsEntityTypeGroupDetail,
    element: (
      <RouteGate
        path={RoutePathType.DocumentsEntityTypeGroupDetail}
        permissions={[
          { resourceId: 'entity_groups', resourceType: 'view' },
          { resourceId: 'entities', resourceType: 'view' },
          { resourceId: 'entity_types', resourceType: 'view' },
        ]}
        resolvers={createDefaultResolvers(DocumentsEntityTypeGroupDetailPage)}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.documentsEntityTypeGroupDetailTitle',
        defaultMessage: 'Entity details',
        description: 'Title for the documents entity type group detail route',
      }),
      breadcrumbs: [
        {
          title: defineMessage({
            id: 'Routes.documentsTitle',
            defaultMessage: 'Documents',
            description: 'Title for the documents page route',
          }),
          path: RoutePathType.Documents,
        },
        {
          resolver: useEntityTypeNameCrumb,
          path: RoutePathType.DocumentsEntityType,
        },
        { resolver: useEntityGroupNameCrumb },
      ],
    },
  },
  {
    path: RoutePathType.DocumentsFolder,
    element: (
      <RouteGate
        path={RoutePathType.DocumentsFolder}
        permissions={[
          {
            resourceId: 'documents',
            resourceType: 'view',
          },
        ]}
        resolvers={[
          {
            permission: checkIsWorkflowOnly,
            redirectPath: RoutePathType.Home,
          },
          {
            component: DocumentsPage,
          },
        ]}
      />
    ),
    handle: {
      // Deferred: the folder path is arbitrarily deep and lives in page state,
      // so the page portals its own trail into the breadcrumb bar. See
      // `RouteBreadcrumbs`.
      breadcrumbs: 'deferred',
      title: defineMessage({
        id: 'Routes.documentsFolderTitle',
        defaultMessage: 'Documents',
        description: 'Title for the documents folder page route',
      }),
    },
  },
  {
    path: RoutePathType.Document,
    element: (
      <RouteGate
        path={RoutePathType.Document}
        permissions={[]}
        resolvers={createDefaultResolvers(DocumentsViewPage)}
      />
    ),
    handle: {
      // Deferred: the folder path is arbitrarily deep and lives in page state,
      // so the page portals its own trail into the breadcrumb bar. See
      // `RouteBreadcrumbs`.
      breadcrumbs: 'deferred',
      title: defineMessage({
        id: 'Routes.documentDetailsTitle',
        defaultMessage: 'Document Details',
        description: 'Title for the document details page route',
      }),
    },
  },
  {
    path: RoutePathType.DocumentRedirect,
    element: (
      <RouteGate
        path={RoutePathType.DocumentRedirect}
        permissions={[]}
        resolvers={createDefaultResolvers(DocumentRedirect)}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.documentRedirectTitle',
        defaultMessage: 'Document Details',
        description: 'Title for the document redirect page route',
      }),
      breadcrumbs: 'none',
    },
  },
  {
    path: RoutePathType.DownloadEmailAttachmentPage,
    element: (
      <RouteGate
        path={RoutePathType.DownloadEmailAttachmentPage}
        resolvers={createDefaultResolvers(DownloadEmailAttachmentPage)}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.downloadEmailAttachmentTitle',
        defaultMessage: 'Download Email Attachment',
        description: 'Title for the download email attachment page route',
      }),
      breadcrumbs: 'self',
    },
  },
  {
    path: RoutePathType.DownloadExportReportPage,
    element: (
      <RouteGate
        path={RoutePathType.DownloadExportReportPage}
        resolvers={createDefaultResolvers(DownloadExportReportPage)}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.downloadExcelExportTitle',
        defaultMessage: 'Download Excel Export',
        description: 'Title for the download Excel export page route',
      }),
      breadcrumbs: 'self',
    },
  },
  {
    path: RoutePathType.FoldersPermissionCheck,
    element: (
      <RouteGate
        path={RoutePathType.FoldersPermissionCheck}
        resolvers={createDefaultResolvers(PermissionFromSSOPage)}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.foldersPermissionCheckTitle',
        defaultMessage: 'Folders Permission Check',
        description: 'Title for the folders permission check page route',
      }),
      breadcrumbs: 'self',
    },
  },
  {
    path: RoutePathType.ManagedEntities,
    element: (
      <RouteGate
        path={RoutePathType.ManagedEntities}
        resolvers={[
          {
            permission: canUseManagedEntities,
            component: ManagedEntityPartiesPage,
          },
          // TODO: add a default redirect
        ]}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.managedEntityPartiesTitle',
        defaultMessage: 'managed entity parties',
        description: 'Title for the managed entity parties page route',
      }),
      breadcrumbs: 'self',
    },
  },
  {
    path: RoutePathType.MyDeletedDocuments,
    element: (
      <RouteGate
        path={RoutePathType.MyDeletedDocuments}
        permissions={[
          {
            resourceId: 'documents',
            resourceType: 'view',
          },
        ]}
        resolvers={createPassthroughResolvers(DocumentsRestore)}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.myDeletedItemsTitle',
        defaultMessage: 'My Deleted Items',
        description: 'Title for the my deleted items page route',
      }),
      breadcrumbs: 'self',
    },
  },
  {
    path: RoutePathType.Playbooks,
    element: (
      <RouteGate
        path={RoutePathType.Playbooks}
        permissions={[
          {
            resourceId: 'clause_library',
            resourceType: 'view',
          },
        ]}
        resolvers={[
          {
            permission: canUsePlaybooks,
            component: PlaybooksRoot,
          },
          {
            redirectPath: RoutePathType.ClauseLibrary,
          },
        ]}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.playbooksTitle',
        defaultMessage: 'Playbooks',
        description: 'Title for the playbooks page route',
      }),
      breadcrumbs: libraryTrail,
    },
    children: [
      {
        path: '',
        element: (
          <Suspense fallback={null}>
            <PlaybooksListPage />
          </Suspense>
        ),
        handle: {
          title: defineMessage({
            id: 'Routes.playbooksTitle',
            defaultMessage: 'Playbooks',
            description: 'Title for the playbooks page route',
          }),
          breadcrumbs: libraryTrail,
        },
      },
      {
        path: RoutePathType.PlaybookDetails,
        element: (
          <Suspense fallback={null}>
            <PlaybookDetailsPage />
          </Suspense>
        ),
        handle: {
          title: defineMessage({
            id: 'Routes.playbooksTitle',
            defaultMessage: 'Playbooks',
            description: 'Title for the playbooks page route',
          }),
          layout: 'bounded',
          breadcrumbs: [
            ...libraryTrail,
            {
              title: defineMessage({
                id: 'Routes.playbooksTitle',
                defaultMessage: 'Playbooks',
                description: 'Title for the playbooks page route',
              }),
              path: RoutePathType.Playbooks,
            },
            { resolver: usePlaybookNameCrumb },
          ],
        } satisfies RouteHandle,
      },
    ],
  },
  {
    path: RoutePathType.Guidelines,
    element: (
      <RouteGate
        path={RoutePathType.Guidelines}
        permissions={[
          {
            resourceId: 'clause_library',
            resourceType: 'view',
          },
        ]}
        resolvers={[
          {
            permission: canUsePlaybooks,
            component: GuidelinesRoot,
          },
          {
            redirectPath: RoutePathType.ClauseLibrary,
          },
        ]}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.AiInstructionsTitle',
        defaultMessage: 'Negotiation Requirements',
        description: 'Title for the Negotiation Requirements page route',
      }),
      breadcrumbs: libraryTrail,
    },
    children: [
      {
        path: '',
        element: (
          <Suspense fallback={null}>
            <GuidelinesListPage />
          </Suspense>
        ),
        handle: {
          title: defineMessage({
            id: 'Routes.AiInstructionsTitle',
            defaultMessage: 'Negotiation Requirements',
            description: 'Title for the Negotiation Requirements page route',
          }),
          breadcrumbs: libraryTrail,
        },
      },
      {
        path: RoutePathType.GuidelineDetails,
        element: (
          <Suspense fallback={null}>
            <GuidelineDetailsPage />
          </Suspense>
        ),
        handle: {
          title: defineMessage({
            id: 'Routes.AiInstructionsTitle',
            defaultMessage: 'Negotiation Requirements',
            description: 'Title for the Negotiation Requirements page route',
          }),
          breadcrumbs: libraryTrail,
        },
      },
    ],
  },
  {
    path: RoutePathType.SavedSearches,
    element: (
      <RouteGate
        path={RoutePathType.SavedSearches}
        permissions={[
          {
            resourceId: 'documents',
            resourceType: 'view',
          },
        ]}
        resolvers={[
          {
            component: SavedSearches,
          },
          {
            redirectPath: RoutePathType.Home,
          },
        ]}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.savedSearchesTitle',
        defaultMessage: 'Saved Searches',
        description: 'Title for the saved searches page route',
      }),
      breadcrumbs: searchTrail,
    },
  },
  {
    path: RoutePathType.SavedDashboards,
    element: (
      <RouteGate
        path={RoutePathType.SavedDashboards}
        permissions={[
          {
            resourceId: 'workflows_dashboard',
            resourceType: 'view',
          },
          {
            resourceId: 'documents_dashboard',
            resourceType: 'view',
          },
        ]}
        resolvers={[
          {
            component: SavedDashboards,
          },
        ]}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.savedDashboardsTitle',
        defaultMessage: 'Saved Dashboards',
        description: 'Title for the saved dashboards page route',
      }),
      breadcrumbs: dashboardsTrail,
    },
  },
  {
    path: RoutePathType.SavedSearch,
    element: (
      <RouteGate
        path={RoutePathType.SavedSearch}
        permissions={[
          {
            resourceId: 'documents',
            resourceType: 'view',
          },
        ]}
        resolvers={[
          {
            component: SearchV2,
          },
          {
            redirectPath: RoutePathType.Search,
          },
        ]}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.searchTitle',
        defaultMessage: 'Search',
        description: 'Title for the search page route',
      }),
      breadcrumbs: [...searchTrail, { resolver: useSavedSearchNameCrumb }],
    },
  },
  {
    path: RoutePathType.Search,
    element: (
      <RouteGate
        path={RoutePathType.Search}
        permissions={[
          {
            resourceId: 'documents',
            resourceType: 'view',
          },
        ]}
        resolvers={[
          {
            component: SearchV2,
          },
        ]}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.searchTitle2',
        defaultMessage: 'Search',
        description: 'Title for the search page route',
      }),
      breadcrumbs: 'self',
    },
  },
  {
    path: RoutePathType.Settings,
    element: (
      <RouteGate
        path={RoutePathType.Settings}
        resolvers={createPassthroughResolvers(SettingsPage)}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.settingsTitle',
        defaultMessage: 'Settings',
        description: 'Title for the settings page route',
      }),
      breadcrumbs: 'self',
    },
  },
  {
    path: RoutePathType.SuperAdminUsers,
    element: (
      <RouteGate
        path={RoutePathType.SuperAdminUsers}
        resolvers={createSuperAdminResolvers(SuperAdminUsersPage)}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.superAdminUsersTitle',
        defaultMessage: 'Users',
        description: 'Title for the super admin users page route',
      }),
      breadcrumbs: [
        {
          title: defineMessage({
            id: 'AdminPage.pageTitle',
            defaultMessage: 'Manage Clients',
            description: 'Page title for admin clients management',
          }),
          path: RoutePathType.Admin,
        },
      ],
    },
  },
  {
    path: RoutePathType.Upload,
    element: (
      <RouteGate
        path={RoutePathType.Upload}
        permissions={[
          {
            resourceId: 'documents',
            resourceType: 'view',
          },
        ]}
        resolvers={[
          {
            permission: canUseUpload,
            component: UploadPage,
          },
          {
            redirectPath: RoutePathType.Home,
          },
        ]}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.uploadTitle',
        defaultMessage: 'Upload',
        description: 'Title for the upload page route',
      }),
      breadcrumbs: 'self',
    },
  },
  {
    path: RoutePathType.WorkflowBuilder,
    element: (
      <RouteGate
        path={RoutePathType.WorkflowBuilder}
        permissions={[
          {
            resourceId: 'workflow_templates',
            resourceType: 'view',
          },
        ]}
        resolvers={createWorkflowAdminResolvers(WorkflowBuilderDashboardPage)}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.workflowBuilderTitle',
        defaultMessage: 'Workflow Builder',
        description: 'Title for the workflow builder page route',
      }),
      breadcrumbs: workflowTrail,
    },
  },
  {
    path: RoutePathType.WorkflowBuilderWorkflow,
    element: (
      <RouteGate
        path={RoutePathType.WorkflowBuilderWorkflow}
        permissions={[
          {
            resourceId: 'workflow_templates',
            resourceType: 'view',
          },
        ]}
        resolvers={createWorkflowAdminResolvers(WorkflowBuilderPage)}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.workflowBuilderWorkflowTitle',
        defaultMessage: 'Workflow Builder',
        description: 'Title for the workflow builder workflow page route',
      }),
      breadcrumbs: [
        ...workflowTrail,
        {
          title: defineMessage({
            id: 'Routes.workflowBuilderTitle',
            defaultMessage: 'Workflow Builder',
            description: 'Title for the workflow builder page route',
          }),
          path: RoutePathType.WorkflowBuilder,
        },
        { resolver: useLegacyWorkflowNameCrumb },
      ],
    },
  },
  {
    path: RoutePathType.WorkflowIntakeFormLink,
    element: (
      <RouteGate
        path={RoutePathType.WorkflowIntakeFormLink}
        permissions={[
          {
            resourceId: 'workflow_tickets',
            resourceType: 'edit',
          },
          {
            resourceId: 'workflow_templates',
            resourceType: 'view',
          },
        ]}
        resolvers={createWorkflowResolvers(WizardTicketForm)}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.workflowIntakeFormTitle',
        defaultMessage: 'Workflow Intake Form',
        description: 'Title for the workflow intake form page route',
      }),
      breadcrumbs: 'self',
    },
  },
  {
    path: RoutePathType.IntegrateWorkflowIntakeFormLink,
    element: (
      <RouteGate
        path={RoutePathType.IntegrateWorkflowIntakeFormLink}
        permissions={[
          {
            resourceId: 'workflow_tickets',
            resourceType: 'edit',
          },
          {
            resourceId: 'workflow_templates',
            resourceType: 'view',
          },
        ]}
        resolvers={createIntegrateWorkflowIntakeFormResolvers(
          WssIntakeFormRedirect,
          WizardTicketForm,
        )}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.workflowIntakeFormTitle',
        defaultMessage: 'Workflow Intake Form',
        description: 'Title for the workflow intake form page route',
      }),
      breadcrumbs: 'self',
    },
  },
  {
    path: RoutePathType.IntegrateWorkflowAmendIntakeForm,
    element: (
      <RouteGate
        path={RoutePathType.IntegrateWorkflowAmendIntakeForm}
        permissions={[
          {
            resourceId: 'workflow_tickets',
            resourceType: 'edit',
          },
          {
            resourceId: 'workflow_templates',
            resourceType: 'view',
          },
        ]}
        resolvers={createWorkflowResolvers(WizardTicketForm)}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.workflowAmendIntakeFormTitle',
        defaultMessage: 'Amend Intake Form',
        description: 'Title for the amend intake form page route',
      }),
      breadcrumbs: 'self',
    },
  },
  {
    path: RoutePathType.WorkflowTickets,
    element: (
      <RouteGate
        path={RoutePathType.WorkflowTickets}
        permissions={[
          {
            resourceId: 'workflow_tickets',
            resourceType: 'view',
          },
        ]}
        resolvers={[
          {
            permission: checkIsWorkflowOnly,
            component: WorkflowTicketsPage,
          },
          {
            permission: canUseWorkflow,
            component: WorkflowTicketsPage,
          },
          {
            redirectPath: RoutePathType.Home,
          },
        ]}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.workflowTicketsTitle',
        defaultMessage: 'Workflow Tickets',
        description: 'Title for the workflow tickets page route',
      }),
      breadcrumbs: workflowTrail,
    },
  },
  {
    path: RoutePathType.WorkflowTicketsNew,
    element: (
      <RouteGate
        path={RoutePathType.WorkflowTicketsNew}
        permissions={[
          {
            resourceId: 'workflow_tickets',
            resourceType: 'view',
          },
        ]}
        resolvers={[
          {
            permission: checkIsWorkflowOnly,
            component: WorkflowTicketsNewPage,
          },
          {
            permission: canUseWorkflow,
            component: WorkflowTicketsNewPage,
          },
          {
            redirectPath: RoutePathType.Home,
          },
        ]}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.workflowTicketsNewTitle',
        defaultMessage: 'New Ticket',
        description: 'Title for the new ticket page route',
      }),
      breadcrumbs: [
        ...workflowTrail,
        {
          title: defineMessage({
            id: 'Routes.workflowTicketsTitle',
            defaultMessage: 'Workflow Tickets',
            description: 'Title for the workflow tickets page route',
          }),
          path: RoutePathType.WorkflowTickets,
        },
      ],
    },
  },
  {
    path: RoutePathType.WorkflowTicketsReroute,
    element: (
      <RouteGate
        path={RoutePathType.WorkflowTicketsReroute}
        resolvers={[
          {
            permission: checkIsWorkflowOnly,
            component: TicketRerouteRedirectPage,
          },
          {
            permission: canUseWorkflow,
            component: TicketRerouteRedirectPage,
          },
          {
            redirectPath: RoutePathType.Home,
          },
        ]}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.redirectingToTicketTitle',
        defaultMessage: 'Redirecting to Ticket',
        description: 'Title for the redirecting to ticket page route',
      }),
      breadcrumbs: 'none',
    },
  },
  {
    path: RoutePathType.WorkflowTicketsTicket,
    element: (
      <RouteGate
        path={RoutePathType.WorkflowTicketsTicket}
        resolvers={[
          {
            permission: checkIsWorkflowOnly,
            component: TicketsViewPage,
          },
          {
            permission: canUseWorkflow,
            component: TicketsViewPage,
          },
          {
            redirectPath: RoutePathType.Home,
          },
        ]}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.ticketDetailsTitle',
        defaultMessage: 'Ticket Details',
        description: 'Title for the ticket details page route',
      }),
      breadcrumbs: [
        ...workflowTrail,
        {
          title: defineMessage({
            id: 'Routes.workflowTicketsTitle',
            defaultMessage: 'Workflow Tickets',
            description: 'Title for the workflow tickets page route',
          }),
          path: RoutePathType.WorkflowTickets,
        },
        { resolver: useLegacyTicketNameCrumb },
      ],
    },
  },
  {
    path: RoutePathType.WorkflowCLMEvolutionEsignRedirect,
    element: (
      <RouteGate
        path={RoutePathType.WorkflowCLMEvolutionEsignRedirect}
        flag={FlagType.CLMEvolutionPhase1}
        permissions={[
          {
            resourceId: 'workflow_tickets',
            resourceType: 'view',
          },
        ]}
        resolvers={[{ component: WorkflowCLMEvolutionEsignRedirectPage }]}
      />
    ),
    handle: {
      title: esignRedirectTabMessages.title,
      layout: 'chromeless',
      breadcrumbs: 'none',
    },
  },
  {
    path: RoutePathType.WorkflowCLMEvolutionRequestsDetail,
    element: (
      <RouteGate
        path={RoutePathType.WorkflowCLMEvolutionRequestsDetail}
        flag={FlagType.CLMEvolutionPhase1}
        permissions={[
          {
            resourceId: 'workflow_tickets',
            resourceType: 'view',
          },
        ]}
        resolvers={[{ component: WorkflowCLMEvolutionRequestDetailPage }]}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.workflowCLMEvolutionRequestDetailTitle',
        defaultMessage: 'Request Details',
        description:
          'Title for the workflow CLM evolution request details page route',
      }),
      breadcrumbs: [
        ...workflowsClmEvolutionTrail,
        {
          title: defineMessage({
            id: 'Routes.workflowCLMEvolutionTicketsTitle',
            defaultMessage: 'Requests',
            description:
              'Title for the workflow CLM evolution requests page route',
          }),
          path: RoutePathType.WorkflowCLMEvolutionRequests,
        },
        { resolver: useTicketNameCrumb },
      ],
    },
    children: [
      {
        path: 'documents/:documentId/:version',
        element: (
          <Suspense fallback={null}>
            <TicketDetailsDocumentViewer />
          </Suspense>
        ),
        handle: {
          title: defineMessage({
            id: 'Routes.workflowCLMEvolutionRequestDetailTitle',
            defaultMessage: 'Request Details',
            description:
              'Title for the workflow CLM evolution request details page route',
          }),
          breadcrumbs: [
            ...workflowsClmEvolutionTrail,
            {
              title: defineMessage({
                id: 'Routes.workflowCLMEvolutionTicketsTitle',
                defaultMessage: 'Requests',
                description:
                  'Title for the workflow CLM evolution requests page route',
              }),
              path: RoutePathType.WorkflowCLMEvolutionRequests,
            },
            {
              resolver: useTicketNameCrumb,
              path: RoutePathType.WorkflowCLMEvolutionRequestsDetail,
            },
            { resolver: useTicketDocumentNameCrumb },
          ],
        },
      },
    ],
  },
  {
    path: RoutePathType.WorkflowCLMEvolutionRequests,
    element: (
      <RouteGate
        path={RoutePathType.WorkflowCLMEvolutionRequests}
        flag={FlagType.CLMEvolutionPhase1}
        permissions={[
          {
            resourceId: 'workflow_tickets',
            resourceType: 'view',
          },
        ]}
        resolvers={[{ component: WorkflowCLMEvolutionTicketsPage }]}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.workflowCLMEvolutionTicketsTitle',
        defaultMessage: 'Requests',
        description: 'Title for the workflow CLM evolution requests page route',
      }),
      breadcrumbs: workflowsClmEvolutionTrail,
    },
  },
  {
    path: RoutePathType.WorkflowCLMEvolutionNewRequest,
    element: (
      <RouteGate
        path={RoutePathType.WorkflowCLMEvolutionNewRequest}
        flag={FlagType.CLMEvolutionPhase1}
        permissions={[
          {
            resourceId: 'workflow_tickets',
            resourceType: 'view',
          },
        ]}
        resolvers={[{ component: WorkflowCLMEvolutionNewRequestPage }]}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.workflowCLMEvolutionNewRequestTitle',
        defaultMessage: 'New Request',
        description:
          'Title for the workflow CLM evolution new request page route',
      }),
      // The trail's leaf depends on which step the `wf` param selects, which a
      // static declaration can't express — the page portals its own.
      breadcrumbs: 'deferred',
    },
  },
  {
    path: RoutePathType.WorkflowCLMEvolutionWorkflows,
    element: (
      <RouteGate
        path={RoutePathType.WorkflowCLMEvolutionWorkflows}
        flag={FlagType.CLMEvolutionPhase1}
        permissions={[
          {
            resourceId: 'workflow_templates',
            resourceType: 'view',
          },
        ]}
        resolvers={[{ component: WorkflowCLMEvolutionWorkflowsPage }]}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.workflowCLMEvolutionWorkflowsTitle',
        defaultMessage: 'Workflow Builder',
        description: 'Title for the workflow builder page route',
      }),
      breadcrumbs: workflowsClmEvolutionTrail,
    },
  },
  {
    path: RoutePathType.WorkflowCLMEvolutionWorkflowsDetail,
    element: (
      <RouteGate
        path={RoutePathType.WorkflowCLMEvolutionWorkflowsDetail}
        flag={FlagType.CLMEvolutionPhase1}
        permissions={[
          {
            resourceId: 'workflow_templates',
            resourceType: 'view',
          },
        ]}
        resolvers={[{ component: WorkflowCLMEvolutionWorkflowDetailPage }]}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.workflowCLMEvolutionDetailsTitle',
        defaultMessage: 'Workflow Details',
        description: 'Title for the workflow details page route',
      }),
      breadcrumbs: workflowClmEvolutionDetailTrail,
    } satisfies RouteHandle,
    children: [
      {
        path: 'workflow',
        element: (
          <Suspense fallback={null}>
            <BlockCanvas />
          </Suspense>
        ),
        handle: {
          title: defineMessage({
            id: 'Routes.workflowCLMEvolutionDetailsTitle',
            defaultMessage: 'Workflow Details',
            description: 'Title for the workflow details page route',
          }),
          breadcrumbs: workflowClmEvolutionDetailTrail,
        },
      },
      {
        // Templates moved into a modal over the canvas; keep the retired path
        // working for existing links.
        path: 'documents',
        element: <WorkflowDocumentsRedirect />,
        handle: {
          title: defineMessage({
            id: 'Routes.workflowCLMEvolutionDetailsTitle',
            defaultMessage: 'Workflow Details',
            description: 'Title for the workflow details page route',
          }),
          breadcrumbs: workflowClmEvolutionDetailTrail,
        },
      },
      {
        // Settings moved into the builder side panel; keep the retired path
        // working for existing links.
        path: 'settings',
        element: <WorkflowSettingsRedirect />,
        handle: {
          title: defineMessage({
            id: 'Routes.workflowCLMEvolutionSettingsTitle',
            defaultMessage: 'Workflow Settings',
            description: 'Title for the workflow settings page route',
          }),
          breadcrumbs: workflowClmEvolutionDetailTrail,
        },
      },
    ],
  },
  {
    // The CLM Evolution prefix moved from /workflows-clm-evolution to
    // /workflows — keep the retired prefix working for existing links.
    path: '/workflows-clm-evolution/*',
    element: <LegacyClmEvolutionRouteRedirect />,
    handle: {
      title: defineMessage({
        id: 'Routes.legacyClmEvolutionRedirectTitle',
        defaultMessage: 'Redirecting',
        description: 'Title for the retired CLM Evolution route redirect page',
      }),
      breadcrumbs: 'none',
      layout: 'chromeless',
    },
  },
  {
    path: RoutePathType.WorkflowTicketsTicketReviewerVersionNumber,
    element: (
      <RouteGate
        path={RoutePathType.WorkflowTicketsTicketReviewerVersionNumber}
        permissions={[
          {
            resourceId: 'workflow_tickets',
            resourceType: 'view',
          },
        ]}
        resolvers={createWorkflowResolvers(TicketReviewerPage)}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.ticketVersionTitle',
        defaultMessage: 'Ticket Version',
        description: 'Title for the ticket version page route',
      }),
      breadcrumbs: [
        ...workflowTrail,
        {
          title: defineMessage({
            id: 'Routes.workflowTicketsTitle',
            defaultMessage: 'Workflow Tickets',
            description: 'Title for the workflow tickets page route',
          }),
          path: RoutePathType.WorkflowTickets,
        },
        {
          resolver: useLegacyTicketNameCrumb,
          path: RoutePathType.WorkflowTicketsTicket,
        },
      ],
    },
  },
  {
    path: RoutePathType.WordOnlineFile,
    element: (
      <RouteGate
        path={RoutePathType.WordOnlineFile}
        permissions={[
          {
            resourceId: 'workflow_tickets',
            resourceType: 'view',
          },
        ]}
        resolvers={createWorkflowOrClmEvolutionResolvers(WordOnlinePage)}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.wordForTheWebTitle',
        defaultMessage: 'Word for the web',
        description: 'Title for the Word for the web page route',
      }),
      breadcrumbs: 'self',
    },
  },
  {
    path: RoutePathType.XRayLibrary,
    element: (
      <RouteGate
        path={RoutePathType.XRayLibrary}
        flag={FlagType.DocumentXRayPrebuiltModels}
        resolvers={[
          {
            permission: canUseAutomation,
            component: XRayLibrary,
          },
          {
            redirectPath: RoutePathType.Home,
          },
        ]}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.xRayModelLibraryTitle',
        defaultMessage: 'Custom AI Model Library',
        description: 'Title for the Custom AI model library page route',
      }),
      breadcrumbs: customAiTrail,
    },
  },
  {
    path: RoutePathType.XRayLibraryAdmin,
    element: (
      <RouteGate
        path={RoutePathType.XRayLibraryAdmin}
        flag={FlagType.DocumentXRayPrebuiltModelsAdmin}
        resolvers={[
          {
            permission: canUseAutomation,
            component: XRayLibraryAdmin,
          },
          {
            redirectPath: RoutePathType.Home,
          },
        ]}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.xRayModelLibraryManagementTitle',
        defaultMessage: 'Custom AI Model Library Management',
        description:
          'Title for the Custom AI model library management page route',
      }),
      breadcrumbs: [
        ...customAiTrail,
        {
          title: defineMessage({
            id: 'Routes.xRayModelLibraryCrumb',
            defaultMessage: 'Model Library',
            description: 'Breadcrumb text for the Custom AI model library page',
          }),
          path: RoutePathType.XRayLibrary,
        },
        {
          title: defineMessage({
            id: 'Routes.xRayModelLibraryAdminCrumb',
            defaultMessage: 'Admin Management',
            description:
              'Breadcrumb text for the Custom AI model library admin management page',
          }),
        },
      ],
    },
  },
];

const publicRouteList: RouteObject[] = [
  {
    path: RoutePathType.Activation,
    element: (
      <RouteGate
        path={RoutePathType.Activation}
        resolvers={createPassthroughResolvers(ActivationPage)}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.activationTitle',
        defaultMessage: 'Activate Evisort',
        description: 'Title for the activation page route',
      }),
    },
  },
  {
    path: RoutePathType.ChangePassword,
    element: (
      <RouteGate
        path={RoutePathType.ChangePassword}
        resolvers={createPassthroughResolvers(ChangePasswordPage)}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.changePasswordTitle',
        defaultMessage: 'Change password',
        description: 'Title for the change password page route',
      }),
    },
  },
  {
    path: RoutePathType.ExternalDocumentViewer,
    element: (
      <RouteGate
        path={RoutePathType.ExternalDocumentViewer}
        resolvers={createPassthroughResolvers(ExternalDocumentViewerPage)}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.evisortDocumentViewerTitle',
        defaultMessage: 'Evisort Document viewer',
        description: 'Title for the Evisort document viewer page route',
      }),
    },
  },
  {
    path: RoutePathType.ExpiredToken,
    element: (
      <RouteGate
        path={RoutePathType.ExpiredToken}
        resolvers={createPassthroughResolvers(ExpiredTokenPage)}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.expiredTokenTitle',
        defaultMessage: 'Expired token',
        description: 'Title for the expired token page route',
      }),
    },
  },
  {
    path: RoutePathType.ForgotPassword,
    element: (
      <RouteGate
        path={RoutePathType.ForgotPassword}
        resolvers={createPassthroughResolvers(ForgotPasswordPage)}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.resetPasswordTitle',
        defaultMessage: 'Reset password',
        description: 'Title for the reset password page route',
      }),
    },
  },
  {
    path: RoutePathType.Login,
    element: (
      <RouteGate
        path={RoutePathType.Login}
        resolvers={createPassthroughResolvers(LoginPage)}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.logInTitle',
        defaultMessage: 'Log In',
        description: 'Title for the log in page route',
      }),
    },
  },
  {
    path: RoutePathType.LoginEmbed,
    element: (
      <RouteGate
        path={RoutePathType.LoginEmbed}
        resolvers={createPassthroughResolvers(LoginEmbedPage)}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.logInEmbedTitle',
        defaultMessage: 'Log In',
        description: 'Title for the log in embed page route',
      }),
    },
  },
  {
    path: RoutePathType.LoginEmbedSuccess,
    element: (
      <RouteGate
        path={RoutePathType.LoginEmbedSuccess}
        resolvers={createPassthroughResolvers(LoginEmbedSuccessPage)}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.logInSuccessfulTitle',
        defaultMessage: 'Log in successful',
        description: 'Title for the log in successful page route',
      }),
    },
  },
  {
    path: RoutePathType.Unauthorized,
    element: (
      <RouteGate
        path={RoutePathType.Unauthorized}
        resolvers={createPassthroughResolvers(Unauthorized)}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.unauthorizedTitle',
        defaultMessage: 'Unauthorized',
        description: 'Title for the unauthorized page route',
      }),
    },
  },
  {
    path: RoutePathType.TicketEmailApproveConfirmation,
    element: (
      <RouteGate
        path={RoutePathType.TicketEmailApproveConfirmation}
        resolvers={createPassthroughResolvers(TicketEmailApproveConfirmation)}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.ticketApproveViaEmailTitle',
        defaultMessage: 'Ticket Approve Via Email',
        description: 'Title for the ticket approve via email page route',
      }),
    },
  },
  {
    path: RoutePathType.TicketEmailRejectConfirmation,
    element: (
      <RouteGate
        path={RoutePathType.TicketEmailRejectConfirmation}
        resolvers={createPassthroughResolvers(TicketEmailRejectConfirmation)}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.ticketRejectViaEmailTitle',
        defaultMessage: 'Ticket Reject Via Email',
        description: 'Title for the ticket reject via email page route',
      }),
    },
  },
  {
    path: RoutePathType.TicketEmailActionError,
    element: (
      <RouteGate
        path={RoutePathType.TicketEmailActionError}
        resolvers={createPassthroughResolvers(TicketEmailActionError)}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.ticketActionLinkInvalidTitle',
        defaultMessage: 'Ticket Action Link Invalid',
        description: 'Title for the ticket action link invalid page route',
      }),
    },
  },
  {
    path: RoutePathType.TicketEmailApproveComment,
    element: (
      <RouteGate
        path={RoutePathType.TicketEmailApproveComment}
        resolvers={createPassthroughResolvers(TicketEmailApproveComment)}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.ticketApproveWithCommentViaEmailTitle',
        defaultMessage: 'Ticket Approve With Comment Via Email',
        description:
          'Title for the ticket approve with comment via email page route',
      }),
    },
  },
  {
    path: RoutePathType.TicketEmailRejectComment,
    element: (
      <RouteGate
        path={RoutePathType.TicketEmailRejectComment}
        resolvers={createPassthroughResolvers(TicketEmailRejectComment)}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.ticketRejectWithCommentViaEmailTitle',
        defaultMessage: 'Ticket Reject With Comment Via Email',
        description:
          'Title for the ticket reject with comment via email page route',
      }),
    },
  },
  {
    path: RoutePathType.WordOnlineError,
    element: (
      <RouteGate
        path={RoutePathType.WordOnlineError}
        resolvers={createPassthroughResolvers(WordOnlineErrorPage)}
      />
    ),
    handle: {
      title: defineMessage({
        id: 'Routes.wordForTheWebErrorTitle',
        defaultMessage: 'Word for the web Error',
        description: 'Title for the Word for the web error page route',
      }),
    },
  },
];

const notFoundRoute: RouteObject = {
  path: RoutePathType.NotFound,
  element: (
    // The catch-all sits outside both layout routes, so it must provide its
    // own `<main>` landmark to keep the SkipToMainLink target present on 404.
    <main id={MAIN_CONTENT_ID}>
      <RouteGate
        path={RoutePathType.NotFound}
        resolvers={createPassthroughResolvers(NoMatch)}
      />
    </main>
  ),
  handle: {
    title: defineMessage({
      id: 'Routes.notFoundTitle',
      defaultMessage: 'Not found',
      description: 'Title for the not found page route',
    }),
  },
};

export const publicRoutes = publicRouteList;
export const privateRoutes = privateRouteList;

/** These route paths are reserved, and cannot be assigned as client workspaces or URL subroutes */
export const reservedRoutePaths: RoutePathType[] = [
  ...publicRouteList
    .map((route) => route.path as RoutePathType)
    .filter((path): path is RoutePathType => typeof path === 'string'),
  RoutePathType.FoldersPermissionCheck,
];

export const routes: RouteObject[] = [
  { element: <LoggedOutLayout />, children: publicRouteList },
  {
    // Sidebar nav preferences only mean anything once signed in. Scoping the
    // providers here instead of the always-on AppContents shell stops their
    // rolodex query from firing on clientless routes like /login or
    // /activation, where there's no session and it would just 401.
    element: (
      <SidebarNavPreferenceProvider>
        <SidebarNavProvider>
          <LoggedInLayout />
        </SidebarNavProvider>
      </SidebarNavPreferenceProvider>
    ),
    children: privateRouteList,
  },
  notFoundRoute,
];
